import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { useAdminBillingPrices, type AdminBillingInvoiceDetail } from '../../hooks/use-admin-billing'
import { BillingInvoiceBadge } from './BillingInvoiceBadge'
import { invoicePeriod } from './BillingInvoicesTable'
import { formatCents } from '../../hooks/use-admin-billing'

/** BillingInvoiceYear shows what an annual invoice committed: prepaid, invoiced so far, the year's max, and its overages. */
export function BillingInvoiceYear({ invoice }: { invoice: AdminBillingInvoiceDetail }) {
  const { t, i18n } = useTranslation('admin')
  const lang = i18n.language.startsWith('fr') ? 'fr' : 'en'
  const { data: prices } = useAdminBillingPrices()
  const nf = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 3 })
  const codeLabel = (code: string) => {
    const found = prices?.codes.find((c) => c.code === code)
    return found ? (found.label[lang] ?? found.label.en) : code
  }
  const div = (code: string) => prices?.codes.find((c) => c.code === code)?.divisor ?? 1

  return (
    <section className="space-y-4" data-testid="billing-invoice-year">
      <div>
        <h2 className="text-lg font-semibold">{t('billing.invoices.year_title')}</h2>
        <p className="text-sm text-muted-foreground">{t('billing.invoices.year_help')}</p>
      </div>
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('billing.prices.column.code')}</TableHead>
              <TableHead className="text-right">{t('billing.invoices.year.prepaid')}</TableHead>
              <TableHead className="text-right">{t('billing.invoices.year.invoiced')}</TableHead>
              <TableHead className="text-right">{t('billing.invoices.year.max')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoice.year_codes.map((c) => (
              <TableRow key={c.code} data-testid={`billing-year-${c.code}`}>
                <TableCell>{codeLabel(c.code)}</TableCell>
                <TableCell className="text-right tabular-nums">{nf.format(c.prepaid / div(c.code))}</TableCell>
                <TableCell className="text-right tabular-nums">{nf.format(c.invoiced / div(c.code))}</TableCell>
                <TableCell className="text-right tabular-nums">{nf.format(c.year_max / div(c.code))}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <h3 className="text-base font-semibold">{t('billing.invoices.overages_title')}</h3>
      {invoice.overages.length === 0 ? (
        <div className="text-sm text-muted-foreground" data-testid="billing-overages-empty">
          {t('billing.invoices.overages_empty')}
        </div>
      ) : (
        <ul className="space-y-1" data-testid="billing-overages">
          {invoice.overages.map((o) => (
            <li key={o.uid} className="flex items-center gap-3 text-sm">
              <Link to="/admin/billing/invoices/$uid" params={{ uid: o.uid }} className="text-primary hover:underline" data-testid={`billing-overage-link-${o.uid}`}>
                {t('billing.invoices.kind.annual_overage')} {invoicePeriod(o)}
              </Link>
              <span className="tabular-nums">{formatCents(o.total_cents, i18n.language)}</span>
              <BillingInvoiceBadge state={o.payment_state} />
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
